import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ChatSignalRService {
  readonly mensagens$ = new Subject<Record<string, unknown>>();
  readonly notificacoes$ = new Subject<Record<string, unknown>>();
  readonly reservas$ = new Subject<Record<string, unknown>>();
  readonly reconectado$ = new Subject<void>();

  private conexao?: HubConnection;
  private iniciando?: Promise<void>;
  private usos = 0;
  private conversas = new Set<string>();
  private clientes = new Set<string>();
  private inboxExecutivo = '';
  private inboxOperacional = false;

  async usar(): Promise<void> {
    this.usos++;
    await this.garantir();
  }

  async liberar(): Promise<void> {
    this.usos = Math.max(0, this.usos - 1);
    if (this.usos > 0 || !this.conexao) {
      return;
    }

    const conexao = this.conexao;
    this.conexao = undefined;
    this.conversas.clear();
    this.clientes.clear();
    this.inboxExecutivo = '';
    this.inboxOperacional = false;
    try {
      await conexao.stop();
    } catch {
      // A tela já saiu. O histórico continua na API.
    }
  }

  async entrarConversa(conversaId: string): Promise<void> {
    const id = this.normalizar(conversaId);
    if (!id) {
      return;
    }
    this.conversas.add(id);
    await this.invocar('EntrarConversa', id);
  }

  async entrarCliente(clienteId: string): Promise<void> {
    const id = this.normalizar(clienteId);
    if (!id) {
      return;
    }
    this.clientes.add(id);
    await this.invocar('EntrarClienteOperacional', id);
  }

  async entrarInboxExecutivo(executivoId: string): Promise<void> {
    const id = this.normalizar(executivoId);
    if (!id) {
      return;
    }
    this.inboxExecutivo = id;
    await this.invocar('EntrarInboxExecutivo', id);
  }

  async entrarInboxOperacional(): Promise<void> {
    this.inboxOperacional = true;
    if (this.conexao?.state !== HubConnectionState.Connected) {
      return;
    }
    try {
      await this.conexao.invoke('EntrarInboxOperacional');
    } catch {
      // Sem o hub o histórico do chat continua na tela do cliente.
    }
  }

  private async garantir(): Promise<void> {
    if (this.conexao?.state === HubConnectionState.Connected) {
      return;
    }
    if (this.iniciando) {
      await this.iniciando;
      return;
    }

    this.iniciando = this.abrir();
    try {
      await this.iniciando;
    } finally {
      this.iniciando = undefined;
    }
  }

  private async abrir(): Promise<void> {
    const conexao = new HubConnectionBuilder()
      .withUrl(`${environment.hubUrl}/notificacaoHub/chat`, {
        withCredentials: true,
        transport: 4
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    conexao.on('MensagemChat', (payload: Record<string, unknown>) => {
      this.mensagens$.next(payload || {});
    });
    conexao.on('NotificacaoChat', (payload: Record<string, unknown>) => {
      this.notificacoes$.next(payload || {});
    });
    conexao.on('ReservaChat', (payload: Record<string, unknown>) => {
      this.reservas$.next(payload || {});
    });
    conexao.onreconnected(() => {
      void this.reentrar().then(() => this.reconectado$.next());
    });

    this.conexao = conexao;
    await conexao.start();
    await this.reentrar();
  }

  private async reentrar(): Promise<void> {
    for (const id of this.conversas) {
      await this.invocar('EntrarConversa', id);
    }
    for (const id of this.clientes) {
      await this.invocar('EntrarClienteOperacional', id);
    }
    if (this.inboxExecutivo) {
      await this.invocar('EntrarInboxExecutivo', this.inboxExecutivo);
    }
    if (this.inboxOperacional && this.conexao?.state === HubConnectionState.Connected) {
      try {
        await this.conexao.invoke('EntrarInboxOperacional');
      } catch {
        // A reconexão tenta de novo no próximo ciclo.
      }
    }
  }

  private async invocar(metodo: string, id: string): Promise<void> {
    if (this.conexao?.state !== HubConnectionState.Connected) {
      return;
    }
    try {
      await this.conexao.invoke(metodo, id);
    } catch {
      // Sem o hub a mensagem continua no histórico ao recarregar.
    }
  }

  private normalizar(valor: string): string {
    return (valor || '').trim().toLowerCase();
  }
}
